import { Tier } from '@mondaygpt/constants'
import { Offering, Package } from '@revenuecat/purchases-js'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Sparkles, Zap } from 'lucide-react'
import { useState } from 'react'
import {
  BillingPeriod,
  formatMonthlyPrice,
  formatYearlyTotal,
  getPackageForPlan,
  hasFreeTrial,
  PLANS_CONFIG,
} from '../utils/plan-pricing'

const priceTransition = { type: 'spring' as const, stiffness: 300, damping: 25 }
const fadeTransition = { duration: 0.2, ease: 'easeInOut' as const }

interface PlanPickerProps {
  offering: Offering | null
  onSelectPlan: (pkg: Package | null, tier: Tier, period: BillingPeriod) => void
  isProcessing?: boolean
  errorMessage?: string | null
}

export function PlanPicker({
  offering,
  onSelectPlan,
  isProcessing,
  errorMessage,
}: PlanPickerProps) {
  const [period, setPeriod] = useState<BillingPeriod>('yearly')

  return (
    <div className="w-full">
      <div className="mb-6 space-y-2 text-center md:text-left">
        {/* `.t-h1` is layer-less and outweighs Tailwind `font-*` utilities,
            so use `!` to flip weights per-breakpoint here. */}
        <h1 className="t-h1 font-bold! md:font-normal!">
          <span className="md:font-normal!">Choose your </span>
          <span className="font-bold!">plan</span>
        </h1>
        <p className="t-body-sm text-paper">
          Professional branded content for your business. In seconds.
        </p>
      </div>

      {/* Period Toggle */}
      <div className="mx-auto mb-6 flex w-fit items-center rounded-full border border-line-bright bg-bg-3 p-1">
        <button
          onClick={() => setPeriod('monthly')}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
            period === 'monthly'
              ? 'bg-bg-2 text-paper shadow-[var(--shadow-card)]'
              : 'text-paper-dim hover:text-paper'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setPeriod('yearly')}
          className={`relative rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
            period === 'yearly'
              ? 'bg-bg-2 text-paper shadow-[var(--shadow-card)]'
              : 'text-paper-dim hover:text-paper'
          }`}
        >
          Yearly
          <span className="text-success absolute -top-2 -right-2 rounded-full bg-[color:var(--success-tint)] px-1.5 py-0.5 text-[10px] font-semibold">
            20% off
          </span>
        </button>
      </div>

      {/* Plan Cards */}
      <div className="flex flex-col gap-3 sm:flex-row">
        {PLANS_CONFIG.map(plan => {
          const pkg = getPackageForPlan(offering, plan.tier, period)
          const price = formatMonthlyPrice(
            pkg,
            period === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
            period
          )
          const yearlyPkg = getPackageForPlan(offering, plan.tier, 'yearly')
          const yearlyTotal = formatYearlyTotal(yearlyPkg, plan.yearlyTotal)
          const showYearly = period === 'yearly'
          // RC strips the trial phase per customer (any past trial start
          // disqualifies) — only claim the trial when it survived. A null
          // package means the offering didn't load (config-fallback prices),
          // not "stripped": keep the historical trial copy then.
          const showTrial = showYearly && (yearlyPkg ? hasFreeTrial(yearlyPkg) : true)

          return (
            <div
              key={plan.tier}
              className={`relative flex flex-1 flex-col rounded-2xl border p-5 transition-shadow ${
                plan.highlighted
                  ? 'border-primary shadow-md ring-1 ring-primary/20'
                  : 'border-line bg-bg-2 hover:border-line-bright hover:shadow-[var(--shadow-card)]'
              }`}
            >
              {plan.highlighted && (
                <div className="text-ink-on-acid absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-acid px-3 py-0.5 text-xs font-semibold">
                  Most Popular
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-paper">{plan.name}</h3>

                {/* Animated price */}
                <div className="mt-1 flex items-baseline gap-1">
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={price}
                      className="text-3xl font-bold text-paper"
                      initial={{ y: 12, opacity: 0, filter: 'blur(4px)' }}
                      animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
                      exit={{ y: -12, opacity: 0, filter: 'blur(4px)' }}
                      transition={priceTransition}
                    >
                      {price}
                    </motion.span>
                  </AnimatePresence>
                  <span className="text-sm text-paper-dim">/mo</span>
                </div>

                {/* Animated yearly details */}
                <div className="mt-1 min-h-[2.5rem]">
                  <AnimatePresence>
                    {showYearly && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={fadeTransition}
                      >
                        <p className="text-xs text-paper-faint">{yearlyTotal}/yr billed annually</p>
                        <p className="text-success mt-0.5 text-xs font-medium">
                          {showTrial
                            ? `7-day free trial · save ${plan.annualSavings}/yr`
                            : `Save ${plan.annualSavings}/yr`}
                        </p>
                        <p className="text-success text-xs font-medium">
                          +{plan.annualBonusCredits} bonus credits (a {plan.annualBonusValue} value)
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <ul className="mb-5 flex-1 space-y-2">
                <li className="flex items-center gap-2 text-sm text-paper">
                  <Sparkles size={14} className="flex-shrink-0 text-acid" />
                  <span className="font-medium">{plan.credits} AI generation credits/month</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-paper">
                  <Check size={14} className="flex-shrink-0 text-paper-faint" />
                  {plan.seats} team seats
                </li>
                {plan.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-paper">
                    <Check size={14} className="flex-shrink-0 text-paper-faint" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Animated CTA label */}
              {plan.highlighted ? (
                <button
                  onClick={() => onSelectPlan(pkg, plan.tier, period)}
                  disabled={isProcessing}
                  className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-xl bg-acid px-4 py-2.5 text-sm font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px hover:shadow-[var(--shadow-acid-cta-hover)] disabled:opacity-50"
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-black/20 border-t-black/80" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      <Zap size={14} />
                      <AnimatePresence mode="popLayout">
                        <motion.span
                          key={showTrial ? 'trial' : 'get'}
                          initial={{ y: 8, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -8, opacity: 0 }}
                          transition={fadeTransition}
                        >
                          {showTrial ? 'Start my free trial' : `Get ${plan.name}`}
                        </motion.span>
                      </AnimatePresence>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => onSelectPlan(pkg, plan.tier, period)}
                  disabled={isProcessing}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-acid px-4 py-2.5 text-sm font-semibold text-acid transition-all hover:bg-[var(--acid-tint)] disabled:opacity-50"
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-2 text-paper">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-line-bright border-t-acid" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      <Zap size={14} className="text-acid" />
                      <AnimatePresence mode="popLayout">
                        <motion.span
                          key={showTrial ? 'trial' : 'get'}
                          initial={{ y: 8, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -8, opacity: 0 }}
                          transition={fadeTransition}
                        >
                          {showTrial ? 'Start my free trial' : `Get ${plan.name}`}
                        </motion.span>
                      </AnimatePresence>
                    </>
                  )}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {errorMessage && (
        <div className="alert mt-4" data-tone="error" role="alert">
          <p className="text-[13px]">{errorMessage}</p>
        </div>
      )}

      {/* Terms */}
      <div className="mt-5 text-center text-xs text-paper-faint">
        By proceeding, you agree to the{' '}
        <a
          href="https://taka.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-acid hover:underline"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://taka.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-acid hover:underline"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  )
}

export type { BillingPeriod }
export { getPackageForPlan }
